import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  HandCoins, Search, Eye, ExternalLink, X, Download, Loader2, Banknote, Info,
} from "lucide-react";
import AdminLayout from "../components/layout/AdminLayout";
import { useCommissionsStore, type Commission, type CommissionFilters } from "../store/useCommissionsStore";
import { useAuthStore } from "../store/useAuthStore";
import { axiosInstance } from "../lib/axios";
import CommissionSettlements from "../components/admin/CommissionSettlements";
import toast from "react-hot-toast";

const pkr = (n: number) => `₨${Math.round(n || 0).toLocaleString()}`;
const usd = (n: number) => `$${(n || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
const fmtDate = (d?: string | null) => (d ? new Date(d).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "—");

const statusStyle = (s: string) => ({
  Pending: "bg-slate-100 text-slate-600 ring-slate-200",
  Locked: "bg-amber-50 text-amber-700 ring-amber-200",
  Paid: "bg-emerald-50 text-emerald-700 ring-emerald-200",
}[s] || "bg-slate-100 text-slate-600 ring-slate-200");

export default function AdminCommissionsPage() {
  const navigate = useNavigate();
  const { commissions, kpis, loading, fetchCommissions, fetchKpis, processPayroll } = useCommissionsStore();
  const { authUser }: any = useAuthStore();
  const canWrite = ["Admin", "Supervisor", "Sales Director"].includes(authUser?.role);

  const [filters, setFilters] = useState<CommissionFilters>({});
  const [viewing, setViewing] = useState<Commission | null>(null);
  const [exporting, setExporting] = useState(false);
  const [processing, setProcessing] = useState<string>("");

  useEffect(() => {
    const t = setTimeout(() => { fetchCommissions(filters); fetchKpis(filters); }, 250);
    return () => clearTimeout(t);
  }, [filters, fetchCommissions, fetchKpis]);

  const setF = (k: keyof CommissionFilters, v: string) => setFilters((p) => ({ ...p, [k]: v || undefined }));

  const clientOptions = useMemo(() => {
    const map = new Map<string, string>();
    commissions.forEach((c) => { if (c.client) map.set(c.client._id, c.client.name); });
    return [...map.entries()].map(([_id, name]) => ({ _id, name })).sort((a, b) => a.name.localeCompare(b.name));
  }, [commissions]);

  const exportCsv = async () => {
    setExporting(true);
    try {
      const res = await axiosInstance.get("/commissions/export", { params: filters, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv;charset=utf-8;" }));
      const a = document.createElement("a");
      a.href = url; a.download = `commissions-${new Date().toISOString().slice(0, 10)}.csv`; a.click();
      URL.revokeObjectURL(url);
      toast.success("Commissions exported");
    } catch { toast.error("Export failed"); } finally { setExporting(false); }
  };

  const doPayroll = async (c: Commission) => {
    setProcessing(c._id);
    await processPayroll(c._id);
    setProcessing("");
  };

  return (
    <AdminLayout>
      <div className="p-4 lg:p-6 max-w-[1500px] mx-auto space-y-5">
        {/* Header */}
        <div className="flex items-end justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-[22px] font-bold text-slate-900 tracking-tight flex items-center gap-2">
              <HandCoins size={22} className="text-[#0058c3]" /> Commission Management
            </h1>
            <p className="text-[13px] text-slate-500 mt-0.5">BD commissions from released milestone payments — calculation, payroll processing &amp; ERP sync.</p>
          </div>
          <button onClick={exportCsv} disabled={exporting} className="inline-flex items-center gap-2 px-3.5 py-2.5 border border-slate-200 rounded-lg text-[13px] font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50">
            {exporting ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />} Export CSV
          </button>
        </div>

        {/* Commission Overview — KPI cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <Kpi label="Pending Commission" value={pkr(kpis?.pending || 0)} tone="text-slate-600" />
          <Kpi label="Locked Commission" value={pkr(kpis?.locked || 0)} tone="text-amber-700" />
          <Kpi label="Paid Commission" value={pkr(kpis?.paid || 0)} tone="text-emerald-700" />
          <Kpi label="Total Commission Earned" value={pkr(kpis?.totalEarned || 0)} tone="text-[#0058c3]" highlight />
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4 flex flex-wrap items-center gap-3">
          <div className="flex-1 relative min-w-[200px]">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input value={filters.search || ""} onChange={(e) => setF("search", e.target.value)} placeholder="Search milestone…" className="w-full pl-9 pr-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-[13px] focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </div>
          <input type="date" value={filters.from || ""} onChange={(e) => setF("from", e.target.value)} title="From" className={fInp} />
          <input type="date" value={filters.to || ""} onChange={(e) => setF("to", e.target.value)} title="To" className={fInp} />
          <select value={filters.status || ""} onChange={(e) => setF("status", e.target.value)} className={fInp}>
            <option value="">Status: All</option><option value="Pending">Pending</option><option value="Locked">Locked</option><option value="Paid">Paid</option>
          </select>
          <select value={filters.client || ""} onChange={(e) => setF("client", e.target.value)} className={fInp}>
            <option value="">Contact: All</option>{clientOptions.map((c) => <option key={c._id} value={c._id}>{c.name}</option>)}
          </select>
          {Object.keys(filters).length > 0 && <button onClick={() => setFilters({})} className="text-[12px] text-slate-500 hover:text-slate-800 font-semibold">Clear</button>}
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left text-[13px] min-w-[1000px]">
              <thead className="bg-slate-50/80 border-b border-slate-200 text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
                <tr>{["Business Developer", "Project", "Contact", "Milestone", "Released", "Commission", "Status", "Payroll Batch", "Updated", ""].map((h) => <th key={h} className="px-4 py-3 whitespace-nowrap">{h}</th>)}</tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {loading && commissions.length === 0 ? (
                  <tr><td colSpan={10} className="px-4 py-14 text-center text-slate-400">Loading…</td></tr>
                ) : commissions.length === 0 ? (
                  <tr><td colSpan={10} className="px-4 py-14 text-center text-slate-400">No commissions match the current filters.</td></tr>
                ) : commissions.map((c) => (
                  <tr key={c._id} className="hover:bg-slate-50/60">
                    <td className="px-4 py-3 font-semibold text-slate-900 whitespace-nowrap">{c.businessDeveloper?.fullName || "—"}</td>
                    <td className="px-4 py-3">
                      {c.project ? <button onClick={() => navigate(`/admin/projects?open=${c.project!._id}`)} className="text-[#0058c3] font-semibold hover:underline inline-flex items-center gap-1">{c.project.name}<ExternalLink size={11} /></button> : "—"}
                    </td>
                    <td className="px-4 py-3">
                      {c.client ? <button onClick={() => navigate(`/admin/clients?open=${c.client!._id}`)} className="text-[#0058c3] font-semibold hover:underline inline-flex items-center gap-1">{c.client.name}<ExternalLink size={11} /></button> : "—"}
                    </td>
                    <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{c.milestoneTitle}</td>
                    <td className="px-4 py-3 text-slate-700 font-mono whitespace-nowrap">{usd(c.releasedAmount)}</td>
                    {/* Commission with interactive tooltip */}
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className="group relative inline-flex items-center gap-1 font-bold text-[#0058c3] cursor-help">
                        {pkr(c.commissionAmount)} <Info size={12} className="text-slate-300" />
                        <span className="pointer-events-none absolute left-0 bottom-6 z-30 hidden group-hover:block w-64 p-3 rounded-lg bg-slate-900 text-white text-[11px] font-normal leading-relaxed shadow-xl">
                          <b className="block mb-1 text-[12px]">Commission calculation</b>
                          Released Amount: <b>{usd(c.releasedAmount)}</b><br />
                          Rule: {c.ruleText}<br />
                          <span className="text-slate-300">{usd(c.releasedAmount)} × ₨{c.ratePkrPerUsd}/USD</span><br />
                          = Commission Earned: <b className="text-emerald-300">{pkr(c.commissionAmount)}</b>
                        </span>
                      </span>
                    </td>
                    <td className="px-4 py-3"><span className={`inline-block px-2.5 py-0.5 rounded-full text-[10px] font-bold ring-1 ${statusStyle(c.status)}`}>{c.status}</span></td>
                    <td className="px-4 py-3 text-slate-500 font-mono text-[12px] whitespace-nowrap">{c.payrollBatch || "—"}</td>
                    <td className="px-4 py-3 text-slate-400 text-[12px] whitespace-nowrap">{fmtDate(c.updatedAt)}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1.5">
                        <button onClick={() => setViewing(c)} title="View details" className="p-1.5 rounded-lg text-slate-500 hover:text-blue-600 hover:bg-slate-100"><Eye size={15} /></button>
                        {canWrite && c.status === "Locked" && (
                          <button onClick={() => doPayroll(c)} disabled={processing === c._id} title="Process payroll" className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-emerald-600 text-white text-[11px] font-semibold hover:bg-emerald-700 disabled:opacity-50">
                            {processing === c._id ? <Loader2 size={13} className="animate-spin" /> : <Banknote size={13} />} Process Payroll
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Commission Settlements — record payments made to BDs (Admin / Sales Director) */}
        {canWrite && (
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4">
            <CommissionSettlements />
          </div>
        )}
      </div>

      {viewing && <CommissionDrawer commission={viewing} canWrite={canWrite} onClose={() => setViewing(null)} onPayroll={async () => { const ok = await processPayroll(viewing._id); if (ok) setViewing(null); }} navigate={navigate} />}
    </AdminLayout>
  );
}

const fInp = "px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-[13px] text-slate-600 focus:outline-none focus:ring-2 focus:ring-blue-500";

const Kpi = ({ label, value, tone, highlight }: { label: string; value: string; tone: string; highlight?: boolean }) => (
  <div className={`rounded-xl border shadow-sm p-4 ${highlight ? "bg-gradient-to-br from-blue-50 to-white border-blue-100" : "bg-white border-slate-200"}`}>
    <div className={`text-2xl font-extrabold tabular-nums ${tone}`}>{value}</div>
    <div className="text-[11px] text-slate-500 uppercase tracking-wide font-semibold mt-0.5">{label}</div>
  </div>
);

function CommissionDrawer({ commission: c, canWrite, onClose, onPayroll, navigate }: { commission: Commission; canWrite: boolean; onClose: () => void; onPayroll: () => void; navigate: (p: string) => void }) {
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/30 backdrop-blur-sm" onClick={onClose}>
      <aside onClick={(e) => e.stopPropagation()} className="absolute top-0 right-0 h-full w-full max-w-md bg-white shadow-2xl flex flex-col">
        <div className="p-5 border-b border-slate-200 flex items-start justify-between">
          <div>
            <p className="text-[10px] uppercase font-bold text-slate-400">Commission</p>
            <h2 className="text-lg font-extrabold text-slate-900">{c.businessDeveloper?.fullName}</h2>
            <span className={`inline-block mt-1 px-2.5 py-0.5 rounded-full text-[10px] font-bold ring-1 ${statusStyle(c.status)}`}>{c.status}</span>
          </div>
          <button onClick={onClose} className="p-1.5 text-slate-400 hover:bg-slate-100 rounded-full"><X size={18} /></button>
        </div>
        <div className="flex-1 overflow-y-auto p-5 space-y-5 text-[13px]">
          <div className="rounded-xl bg-blue-50 border border-blue-100 p-4">
            <div className="text-[11px] font-semibold text-blue-700 uppercase">Commission Earned</div>
            <div className="text-2xl font-extrabold text-blue-900">{pkr(c.commissionAmount)}</div>
            <div className="text-[11px] text-slate-500 mt-1">{usd(c.releasedAmount)} × ₨{c.ratePkrPerUsd}/USD · {c.ruleText}</div>
          </div>
          <Row label="Contact" value={c.client?.name} onClick={c.client ? () => navigate(`/admin/clients?open=${c.client!._id}`) : undefined} />
          <Row label="Project" value={c.project?.name} onClick={c.project ? () => navigate(`/admin/projects?open=${c.project!._id}`) : undefined} />
          <Row label="Milestone" value={c.milestoneTitle} />
          <Row label="Released Amount" value={usd(c.releasedAmount)} />
          <Row label="Payroll Batch" value={c.payrollBatch || "—"} />
          <Row label="Payroll Reference" value={c.payrollRef || "—"} />
          <Row label="Processed" value={c.processedAt ? `${fmtDate(c.processedAt)}${c.processedBy?.fullName ? ` · ${c.processedBy.fullName}` : ""}` : "—"} />

          <div>
            <h3 className="text-[11px] font-bold uppercase text-slate-500 mb-2">Status Timeline</h3>
            <div className="space-y-1.5">
              {(c.statusHistory || []).length ? c.statusHistory!.map((h, i) => (
                <div key={i} className="flex items-center justify-between text-[12px] bg-slate-50 rounded px-3 py-1.5">
                  <span className="font-semibold text-slate-700">{h.status}{h.note ? ` · ${h.note}` : ""}</span>
                  <span className="text-slate-400">{fmtDate(h.at)}</span>
                </div>
              )) : <p className="text-[12px] text-slate-400">No history.</p>}
            </div>
          </div>
        </div>
        {canWrite && c.status === "Locked" && (
          <div className="p-4 border-t border-slate-200 bg-slate-50/50">
            <button onClick={onPayroll} className="w-full px-3 py-2.5 text-[13px] font-semibold bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 inline-flex items-center justify-center gap-1.5"><Banknote size={15} /> Process Payroll → Mark Paid</button>
          </div>
        )}
      </aside>
    </div>
  );
}

const Row = ({ label, value, onClick }: { label: string; value?: string | undefined; onClick?: (() => void) | undefined }) => (
  <div className="flex justify-between items-center py-1 border-b border-slate-50">
    <span className="text-slate-500">{label}</span>
    {onClick ? <button onClick={onClick} className="text-[#0058c3] font-semibold hover:underline inline-flex items-center gap-1">{value || "—"}<ExternalLink size={11} /></button> : <span className="font-semibold text-slate-800">{value || "—"}</span>}
  </div>
);
